import React, { useEffect } from "react";
import { useGistDiscussionWorkspaceAdapter } from "./adapters/useGistDiscussionWorkspaceAdapter";
import { CanonicalCommentWorkspace } from "./CanonicalCommentWorkspace";
import type { CanonicalCommentWorkspaceSurface } from "./model/types";
import styles from "./CanonicalCommentWorkspace.module.css";

type GistCommentWorkspacePanelProps = {
  gistId: number;
  editId?: number | null;
  title?: string;
  subtitle?: string;
  user: any;
  surface: CanonicalCommentWorkspaceSurface;
  selectedCommentId?: number | null;
  selectedAnchorId?: number | null;
  onSelectedCommentChange?: (commentId: number | null) => void;
  onSelectedAnchorChange?: (anchorId: number | null) => void;
  onRequestExpand?: () => void;
  onRequestDock?: () => void;
  onRequestClose?: () => void;
};

export const GistCommentWorkspacePanel: React.FC<GistCommentWorkspacePanelProps> = ({
  gistId,
  editId,
  title,
  subtitle,
  user,
  surface,
  selectedCommentId,
  selectedAnchorId,
  onSelectedCommentChange,
  onSelectedAnchorChange,
  onRequestExpand,
  onRequestDock,
  onRequestClose,
}) => {
  const adapter = useGistDiscussionWorkspaceAdapter({
    gistId,
    editId,
    title,
    subtitle,
    user,
    enabled: true,
  });

  useEffect(() => {
    if (surface !== "expanded" || !onRequestClose) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onRequestClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onRequestClose, surface]);

  const isExpanded = surface === "expanded";

  return (
    <div
      className={`${styles.workspacePanelMount} ${
        isExpanded ? styles.workspacePanelMountExpanded : styles.workspacePanelMountDocked
      }`}
      aria-label="Comment workspace panel"
    >
      {isExpanded && (
        <button
          type="button"
          className={styles.workspaceBackdrop}
          onClick={onRequestClose}
          aria-label="Close Comment Workspace"
        />
      )}
      <div
        className={styles.workspacePanelCard}
        role={isExpanded ? "dialog" : undefined}
        aria-modal={isExpanded ? "true" : undefined}
        aria-label={isExpanded ? "Expanded Comment Workspace" : undefined}
      >
        <CanonicalCommentWorkspace
          adapter={adapter}
          surface={surface}
          selectedCommentId={selectedCommentId}
          selectedAnchorId={selectedAnchorId}
          onSelectedCommentChange={onSelectedCommentChange}
          onSelectedAnchorChange={onSelectedAnchorChange}
          onRequestExpand={onRequestExpand}
          onRequestDock={onRequestDock}
          onRequestClose={onRequestClose}
        />
      </div>
    </div>
  );
};
